import React, { ReactNode } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';

import { PersonOrgType } from '@ndvr/auth-api';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { dateRangeOptions, getClientTypeAlias, states, TODAY_DATE_VALUE } from 'utils';
import {
    FilterActions,
    FilterFormLayout,
    FormMultiSelect,
    FormMultiSelectOption,
    FormRadioButtons,
    SearchInput,
    Title,
    VerticalLayout,
} from 'widgets';
import { DropdownLayout } from 'widgets/Dropdown';
import { DateRangeInput } from 'widgets/form/input/FormDateInput';
import { HorizontalLayout } from 'widgets/Layout';
import { Text } from 'widgets/Text';

import { ClientFilterLabels } from './ClientFilterLabels';
import { ClientFilterKeys, ClientFilters, useClientFiltersContext } from './ClientFiltersContext';

type ClientFilterType = 'type' | 'start-date' | 'state';

export const ClientFiltersForm = ({
    centerContent,
    count,
    rightActions,
}: {
    centerContent?: ReactNode;
    count?: number;
    rightActions?: ReactNode;
}) => {
    const formMethods = useForm<ClientFilters>();
    const { isFilterActive, resetFilters } = useClientFiltersContext();

    return (
        <FormProvider {...formMethods}>
            <FilterFormLayout
                centerContent={centerContent}
                count={count}
                countLabel="clients"
                isFilterActive={isFilterActive}
                onResetFilters={() => resetFilters()}
                filterButtons={<ClientMultiFilter />}
                filterLabels={<ClientFilterLabels />}
                rightActions={rightActions}
            />
        </FormProvider>
    );
};

const personOrgTypeOptions: FormMultiSelectOption[] = Object.values(PersonOrgType).map((value: PersonOrgType) => ({
    value,
    label: getClientTypeAlias(value),
}));

const ClientMultiFilter = () => {
    const { filters, personOrgTypeFilterCount, statesFilterCount, startDateFilterCount, resetFilters } =
        useClientFiltersContext();
    const { reset: resetFormState } = useFormContext();

    const sections: MultiFilterSection<ClientFilterType>[] = [
        {
            id: 'type',
            label: 'Type',
            count: personOrgTypeFilterCount,
            onClear: () => resetFilters([ClientFilterKeys.personOrgTypes]),
            content: <PersonOrgTypeFilterContent />,
        },
        {
            id: 'start-date',
            label: 'Start Date',
            count: startDateFilterCount,
            onClear: () => resetFilters([ClientFilterKeys.startDateStart, ClientFilterKeys.startDateEnd]),
            content: <StartDateFilterContent />,
        },
        {
            id: 'state',
            label: 'State',
            count: statesFilterCount,
            onClear: () => resetFilters([ClientFilterKeys.states]),
            content: <StatesFilterContent />,
        },
    ];

    return (
        <MultiFilter
            sections={sections}
            defaultSectionId="type"
            dropdownClassName="h-96 w-[440px] p-0"
            contentClassName="min-h-0 flex-1 overflow-y-auto p-4 pt-3"
            onOpenChange={(open) => {
                if (open) {
                    resetFormState(filters);
                }
            }}
        />
    );
};

const PersonOrgTypeFilterContent = () => {
    const { getValues } = useFormContext();
    const { patchFilters, resetFilters } = useClientFiltersContext();

    return (
        <FilterSectionLayout $gap={16}>
            <Title content>Account Owner Type</Title>
            <FormMultiSelect name={ClientFilterKeys.personOrgTypes} options={personOrgTypeOptions} />
            <FilterActions
                onApply={() =>
                    patchFilters({
                        [ClientFilterKeys.personOrgTypes]: getValues(ClientFilterKeys.personOrgTypes),
                    })
                }
                onReset={() => resetFilters([ClientFilterKeys.personOrgTypes])}
            />
        </FilterSectionLayout>
    );
};

const StatesFilterContent = () => {
    const { getValues } = useFormContext();
    const { patchFilters, resetFilters } = useClientFiltersContext();
    const [searchTerm, setSearchTerm] = React.useState('');

    const stateOptions = React.useMemo(
        () =>
            states.map((state) => ({
                value: state.abbreviation,
                label: `${state.abbreviation} ${state.name}`,
            })),
        [],
    );

    return (
        <DropdownLayout
            top={
                <VerticalLayout $gap={16}>
                    <Title content>US States & Territories</Title>
                    <SearchInput
                        placeholder="Search States"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </VerticalLayout>
            }
            bottom={
                <FilterActions
                    onApply={() =>
                        patchFilters({
                            [ClientFilterKeys.states]: getValues(ClientFilterKeys.states),
                        })
                    }
                    onReset={() => resetFilters([ClientFilterKeys.states])}
                />
            }
        >
            <FormMultiSelect name={ClientFilterKeys.states} options={stateOptions} searchTerm={searchTerm} />
        </DropdownLayout>
    );
};

const StartDateFilterContent = () => {
    const { getValues, setValue } = useFormContext();
    const { resetFilters, patchFilters } = useClientFiltersContext();
    const theme = useTheme();

    return (
        <FilterSectionLayout $gap={16}>
            <Title content>Start Date</Title>
            <DateRangeInput
                title="Custom Date Range"
                startName={ClientFilterKeys.startDateStart}
                endName={ClientFilterKeys.startDateEnd}
                $iconColor={theme.colors.cocoa500}
                $outline
            />
            <HorizontalLayout $rowCenter $gap={8}>
                <Separator />
                <Text $themeColor="subtle">OR</Text>
                <Separator />
            </HorizontalLayout>
            <FormRadioButtons
                title=""
                name={ClientFilterKeys.startDateStart}
                variant="outline"
                values={dateRangeOptions}
                onChange={() => setValue(ClientFilterKeys.startDateEnd, TODAY_DATE_VALUE)}
            />
            <FilterActions
                onApply={() => {
                    patchFilters({
                        [ClientFilterKeys.startDateStart]: getValues(ClientFilterKeys.startDateStart),
                        [ClientFilterKeys.startDateEnd]: getValues(ClientFilterKeys.startDateEnd),
                    });
                }}
                onReset={() => resetFilters([ClientFilterKeys.startDateStart, ClientFilterKeys.startDateEnd])}
            />
        </FilterSectionLayout>
    );
};

const FilterSectionLayout = styled(VerticalLayout)`
    width: 100%;
`;

const Separator = styled.div`
    width: 100%;
    background-color: ${({ theme }) => theme.border.light};
    height: 1px;
`;
